/**
 * LicensesList — Licencias y números de serie (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`), un `styles/licenses.css` con sus propias
 * tarjetas de resumen, borrado con `window.confirm` y la ficha abriendo
 * **directamente en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; el
 * resumen va en el hueco de KPIs del estándar (`kpis`), con chips que **filtran
 * al pulsarlos**; la ficha entra en **lectura** y el borrado usa el diálogo
 * único.
 *
 * Botones propios:
 *  · **Ver el equipo** — a la ficha del CI donde está instalada
 *    (`assigned_ci_type` + `assigned_ci_id`, con `docElementPath()`).
 *  · **Mostrar / ocultar la clave** — en vez de texto en la fila.
 *  · **Copiar la clave** — al portapapeles (con aviso arriba).
 *  · **Copiar** (el del estándar) — alta precargada sin clave ni equipo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  KeyIcon,
  ListBulletIcon,
  Squares2X2Icon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import LicenseForm from './LicenseForm';
import { useListView } from '../views/ViewsContext';
import { getServerFormStyle } from '../utils/serverFormStyle';
import { docElementPath } from '../utils/docElements';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';

const EXPIRY_META = {
  expired:   { label: 'Caducada',      badge: 'list-badge--off' },
  expiring:  { label: 'Caduca pronto', badge: 'list-badge--warn' },
  perpetual: { label: 'Perpetua',      badge: 'list-badge' },
  ok:        { label: 'Vigente',       badge: 'list-badge--on' },
};

const EMPTY_LICENSE = {
  product: '', version: '', license_type: 'per_device', quantity: 1, license_key: '',
  vendor: '', contract_ref: '', purchase_date: '', expiry_date: '', status: 'active',
  assigned_ci_type: '', assigned_ci_id: '', software_installation_id: '', notes: '',
};

/** Enmascara la clave: sólo se ven los últimos 4 caracteres. */
function maskKey(key) {
  if (!key) return '—';
  const limpia = String(key).replace(/\s+/g, '');
  if (limpia.length <= 4) return '••••';
  return `••••-••••-${limpia.slice(-4)}`;
}

export default function LicensesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [licenses, setLicenses] = useState([]);
  const estiloFicha = getServerFormStyle();
  const [summary, setSummary]   = useState(null);
  const [meta, setMeta]         = useState({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterType, setFilterType]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterExpiry, setFilterExpiry]       = useState('');
  const [filterVendor, setFilterVendor]       = useState('');
  const [filterContract, setFilterContract]   = useState('');
  const [filterAssigned, setFilterAssigned]   = useState('');
  const [filterNoKey, setFilterNoKey]         = useState('');
  const [groupMode, setGroupMode]             = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [cloneSource, setCloneSource] = useState(null);
  const [hlId, setHlId]               = useState(null);
  const [showKey, setShowKey]         = useState({});
  const [copied, setCopied]           = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      // Los filtros se aplican en cliente (estándar de listados): se pide todo.
      const [metaResp, list, sum] = await Promise.all([
        api.getLicensesMeta(companyId),
        api.getLicenses(companyId),
        api.getLicensesSummary(companyId).catch(() => null),
      ]);
      setMeta(metaResp || {});
      setLicenses(Array.isArray(list) ? list : []);
      setSummary(sum || null);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las licencias');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?nueva=1&product=...&software_installation_id=... — alta desde el inventario. */
  useEffect(() => {
    if (searchParams.get('nueva') !== '1' || !Object.keys(meta).length) return;
    const product = searchParams.get('product') || '';
    const version = searchParams.get('version') || '';
    const vendor = searchParams.get('vendor') || '';
    const installationId = searchParams.get('software_installation_id') || '';
    setCloneSource(null);
    setInitialMode('create');
    setSelected({
      ...EMPTY_LICENSE,
      product,
      version,
      vendor,
      software_installation_id: installationId,
      assigned_ci_type: searchParams.get('assigned_ci_type') || '',
      assigned_ci_id: searchParams.get('assigned_ci_id') || '',
    });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ['nueva', 'product', 'version', 'vendor', 'software_installation_id',
        'assigned_ci_type', 'assigned_ci_id'].forEach((k) => next.delete(k));
      return next;
    }, { replace: true });
  }, [searchParams, meta, setSearchParams]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !licenses.length) return undefined;
    const id = Number(raw);
    const target = licenses.find(l => l.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setCloneSource(null); setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`lic-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, licenses, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (l) => { setCloneSource(null); setInitialMode('view');   setSelected(l); };
  const openEdit   = (l) => { setCloneSource(null); setInitialMode('edit');   setSelected(l); };
  const openCreate = ()  => { setCloneSource(null); setInitialMode('create'); setSelected({ ...EMPTY_LICENSE }); };
  /** Copiar: producto y tipo se repiten, pero la clave y el equipo no. */
  const openCopy   = (l) => {
    if (!l) return;
    setSelected(null);
    setCloneSource({
      ...l,
      id: undefined,
      product: l.product ? `${l.product} (copia)` : '',
      license_key: '',
      assigned_ci_type: '',
      assigned_ci_id: '',
      purchase_date: '',
    });
    setInitialMode('create');
  };
  const closeForm = () => { setSelected(null); setCloneSource(null); load(); };

  const handleDelete = async (item) => {
    try {
      await api.deleteLicense(item.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  const copyKey = async (l) => {
    try {
      await navigator.clipboard.writeText(l.license_key || '');
      setCopied(l.product);
      setTimeout(() => setCopied(''), 2500);
    } catch {
      setError('El navegador no dejó copiar al portapapeles');
    }
  };

  /* ── etiquetas y opciones ───────────────────────────────────────────────── */
  const typeLabels = useMemo(() => meta.license_type_labels || {}, [meta.license_type_labels]);
  const statusLabels = useMemo(() => meta.license_status_labels || {}, [meta.license_status_labels]);
  const warningDays = summary?.warning_days ?? meta.expiry_warning_days ?? 60;

  const vendorOptions = useMemo(() => {
    const vendors = [...new Set(licenses.map(l => l.vendor).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));
    return vendors.map(v => ({ value: v, label: v }));
  }, [licenses]);

  const contractOptions = useMemo(() => {
    const refs = [...new Set(licenses.map(l => l.contract_ref).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));
    return refs.map(r => ({ value: r, label: r }));
  }, [licenses]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return licenses.filter((l) => {
      if (filterType && l.license_type !== filterType) return false;
      if (filterStatus && l.status !== filterStatus) return false;
      if (filterExpiry && (l.expiry_state || 'ok') !== filterExpiry) return false;
      if (filterVendor && l.vendor !== filterVendor) return false;
      if (filterContract && l.contract_ref !== filterContract) return false;
      if (filterAssigned === 'yes' && !l.assigned_ci_id) return false;
      if (filterAssigned === 'no' && l.assigned_ci_id) return false;
      if (filterNoKey === 'yes' && l.license_key) return false;
      if (filterNoKey === 'no' && !l.license_key) return false;
      if (!term) return true;
      return [
        l.product, l.version, l.vendor, l.contract_ref, l.assigned_ci_name,
        l.license_key, l.notes, typeLabels[l.license_type], statusLabels[l.status],
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [licenses, search, filterType, filterStatus, filterExpiry, filterVendor,
    filterContract, filterAssigned, filterNoKey, typeLabels, statusLabels]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navLicenses = useNavOrder(filtered, selected, selected !== null);

  /* ── KPIs: chips que filtran al pulsarlos (estándar) ─────────────────────── */
  const clearFilters = () => {
    setFilterType(''); setFilterStatus(''); setFilterExpiry('');
    setFilterVendor(''); setFilterContract(''); setFilterAssigned('');
    setFilterNoKey(''); setSearch('');
  };

  const kpis = (
    <div className="list-kpis list-kpis--chips">
      <button
        type="button"
        className="list-kpi-chip"
        onClick={clearFilters}
        title={tr('lic.show_all', 'Ver todas las licencias')}
      >
        <span className="list-kpi-value">{summary?.total ?? licenses.length}</span>
        <span className="list-kpi-label">{tr('lic.plural', 'Licencias')}</span>
      </button>
      <button
        type="button"
        className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('active'); }}
        title={tr('lic.filter_active', 'Ver solo las activas')}
      >
        <span className="list-kpi-value">{summary?.active ?? '—'}</span>
        <span className="list-kpi-label">{tr('lic.active', 'Activas')}</span>
      </button>
      <button
        type="button"
        className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterStatus('in_renewal'); }}
        title={tr('lic.filter_renewal', 'Ver las que están en renovación')}
      >
        <span className="list-kpi-value">{summary?.in_renewal ?? '—'}</span>
        <span className="list-kpi-label">{tr('lic.in_renewal', 'En renovación')}</span>
      </button>
      <button
        type="button"
        className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterExpiry('expiring'); }}
        title={tr('lic.filter_expiring', 'Ver las que caducan pronto')}
      >
        <span className="list-kpi-value">{summary?.expiring ?? '—'}</span>
        <span className="list-kpi-label">{tr('lic.expiring', 'Caducan')} ≤ {warningDays} d</span>
      </button>
      <button
        type="button"
        className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterExpiry('expired'); }}
        title={tr('lic.filter_expired', 'Ver las caducadas')}
      >
        <span className="list-kpi-value">{summary?.expired ?? '—'}</span>
        <span className="list-kpi-label">{tr('lic.expired', 'Caducadas')}</span>
      </button>
      <button
        type="button"
        className="list-kpi-chip"
        onClick={() => { clearFilters(); setFilterExpiry('perpetual'); }}
        title={tr('lic.filter_perpetual', 'Ver las perpetuas')}
      >
        <span className="list-kpi-value">{summary?.perpetual ?? '—'}</span>
        <span className="list-kpi-label">{tr('lic.perpetual', 'Perpetuas')}</span>
      </button>
    </div>
  );

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'product',
        label: tr('lic.product', 'Producto'),
        sortable: true,
        render: (l) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>
              {l.product}{l.version ? ` · ${l.version}` : ''}
            </span>
            {l.contract_ref && <span className="list-hint list-code">{l.contract_ref}</span>}
          </span>
        ),
      },
      {
        key: 'license_type',
        label: tr('virt.type', 'Tipo'),
        sortable: true,
        sortValue: (l) => l.license_type || '',
        render: (l) => (
          <span className="list-badge">
            {l.license_type_label || typeLabels[l.license_type] || l.license_type || dash}
          </span>
        ),
      },
      {
        key: 'license_key',
        label: tr('lic.key', 'Clave / nº de serie'),
        render: (l) => {
          if (!l.license_key) return <span className="list-hint">{tr('lic.no_key', 'Sin clave')}</span>;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className="list-code" style={{ wordBreak: 'break-all' }}>
                {showKey[l.id] ? l.license_key : maskKey(l.license_key)}
              </span>
              <button
                type="button"
                className="list-action-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowKey(prev => ({ ...prev, [l.id]: !prev[l.id] }));
                }}
                title={showKey[l.id] ? tr('lic.hide_key', 'Ocultar la clave') : tr('lic.show_key', 'Mostrar la clave')}
              >
                {showKey[l.id] ? <EyeSlashIcon /> : <EyeIcon />}
              </button>
            </span>
          );
        },
      },
      {
        key: 'quantity',
        label: tr('lic.quantity', 'Cantidad'),
        sortable: true,
        sortValue: (l) => l.quantity ?? 0,
        render: (l) => l.quantity ?? 1,
      },
      {
        key: 'vendor',
        label: tr('lic.vendor', 'Proveedor'),
        sortable: true,
        render: (l) => l.vendor || dash,
      },
      {
        key: 'assigned',
        label: tr('lic.installed_on', 'Instalada en'),
        sortable: true,
        sortValue: (l) => l.assigned_ci_name || '',
        render: (l) => (l.assigned_ci_name
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                const path = docElementPath(l.assigned_ci_type, l.assigned_ci_id);
                if (path) navigate(path);
              }}
              title={tr('lic.view_ci', 'Ver el equipo donde está instalada')}
            >
              {l.assigned_ci_name}
            </button>
          )
          : dash),
      },
      {
        key: 'expiry',
        label: tr('lic.expiry', 'Caducidad'),
        sortable: true,
        sortValue: (l) => l.expiry_date || '',
        render: (l) => {
          const meta_ = EXPIRY_META[l.expiry_state] || EXPIRY_META.ok;
          const texto = !l.expiry_date
            ? tr('lic.no_expiry', 'Sin caducidad')
            : `${l.expiry_date}${l.days_to_expiry != null
              ? (l.days_to_expiry < 0
                ? ` · hace ${Math.abs(l.days_to_expiry)} d`
                : l.days_to_expiry === 0 ? ' · hoy' : ` · en ${l.days_to_expiry} d`)
              : ''}`;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>
              <span className="list-hint">{texto}</span>
            </span>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (l) => l.status || '',
        render: (l) => (
          <span className="list-badge">{l.status_label || statusLabels[l.status] || l.status || dash}</span>
        ),
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (l) => l.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, typeLabels, statusLabels, showKey]);

  const defaultVisible = ['product', 'license_type', 'license_key', 'assigned', 'expiry', 'status'];

  const filters = [
    {
      key: 'type', label: tr('virt.type', 'Tipo'), allLabel: tr('lic.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: (meta.license_types || []).map(v => ({ value: v, label: typeLabels[v] || v })),
    },
    {
      key: 'status', label: tr('lic.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: (meta.license_statuses || []).map(v => ({ value: v, label: statusLabels[v] || v })),
    },
    {
      key: 'expiry', label: tr('lic.expiry', 'Caducidad'), allLabel: tr('lic.expiry_all', 'Cualquier caducidad'),
      value: filterExpiry, onChange: setFilterExpiry,
      options: Object.entries(EXPIRY_META).map(([value, meta_]) => ({ value, label: meta_.label })),
    },
    {
      key: 'vendor', label: tr('lic.vendor', 'Proveedor'), allLabel: tr('lic.vendor_all', 'Cualquier proveedor'),
      value: filterVendor, onChange: setFilterVendor, options: vendorOptions,
    },
    {
      key: 'contract', label: tr('lic.contract', 'Contrato'), allLabel: tr('lic.contract_all', 'Cualquier contrato'),
      value: filterContract, onChange: setFilterContract, options: contractOptions,
    },
    {
      key: 'assigned', label: tr('lic.installed_on', 'Instalada'), allLabel: tr('lic.installed_all', 'Instalada: da igual'),
      value: filterAssigned, onChange: setFilterAssigned,
      options: [
        { value: 'yes', label: tr('lic.installed_yes', 'Instalada en un equipo') },
        { value: 'no', label: tr('lic.installed_no', 'Sin equipo asignado') },
      ],
    },
    {
      key: 'nokey', label: tr('lic.key', 'Clave'), allLabel: tr('lic.key_all', 'Con o sin clave'),
      value: filterNoKey, onChange: setFilterNoKey,
      options: [
        { value: 'yes', label: tr('lic.only_nokey', 'Sin clave registrada') },
        { value: 'no', label: tr('lic.only_key', 'Con clave registrada') },
      ],
    },
    {
      key: 'group', label: tr('lic.group', 'Agrupar por'), allLabel: tr('lic.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'vendor', label: tr('lic.vendor', 'Proveedor') },
        { value: 'type', label: tr('virt.type', 'Tipo') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'vendor') return (l) => l.vendor || tr('lic.no_vendor', 'Sin proveedor');
    if (groupMode === 'type') return (l) => l.license_type_label || typeLabels[l.license_type] || tr('list.no_value', 'Sin valor');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, typeLabels]);

  const subtitle = `${licenses.length} ${tr('lic.plural', 'licencias')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(l => ({
    'Producto': l.product || '',
    'Versión': l.version || '',
    'Tipo': l.license_type_label || typeLabels[l.license_type] || '',
    'Cantidad': l.quantity ?? '',
    'Clave / nº de serie': l.license_key || '',
    'Proveedor': l.vendor || '',
    'Contrato': l.contract_ref || '',
    'Compra': l.purchase_date || '',
    'Caducidad': l.expiry_date || 'perpetua',
    'Días para caducar': l.days_to_expiry ?? '',
    'Situación': (EXPIRY_META[l.expiry_state] || EXPIRY_META.ok).label,
    'Estado': l.status_label || statusLabels[l.status] || '',
    'Instalada en': l.assigned_ci_name || '',
    'Notas': l.notes || '',
  })), [filtered, typeLabels, statusLabels]);

  /** Tarjetas: lo esencial de la licencia, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((l) => {
          const meta_ = EXPIRY_META[l.expiry_state] || EXPIRY_META.ok;
          return (
            <div
              key={l.id}
              id={`lic-card-${l.id}`}
              className={`list-card${l.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(l)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(l); }}
              title={`${tr('action.view', 'Ver')} ${l.product}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{l.product}</div>
                  {l.vendor && <span className="list-hint">{l.vendor}</span>}
                </div>
                <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>
              </div>

              <div className="list-card-meta">
                <span className="list-badge">{l.license_type_label || typeLabels[l.license_type] || '—'}</span>
                {l.expiry_date && <span className="list-hint">{l.expiry_date}</span>}
                {l.assigned_ci_name && <span>{l.assigned_ci_name}</span>}
                {l.quantity > 1 && <span className="list-badge">×{l.quantity}</span>}
              </div>

              <div className="list-card-actions">{rowActions(l)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const LIC_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada licencia'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('licenses', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('lic.select_company', 'Selecciona una empresa para ver las licencias.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {(selected !== null || cloneSource) && (
        <LicenseForm
          license={cloneSource || (selected && selected.id ? selected : null)}
          companyId={companyId}
          meta={meta}
          initialMode={cloneSource ? 'create' : initialMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navLicenses}
          navIndex={!cloneSource && selected && selected.id ? navLicenses.findIndex(l => String(l.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSaved={(creado) => {
            load();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setInitialMode('edit'); setSelected(creado); }
          }}
        />
      )}

      <div style={{ display: (selected !== null || cloneSource) ? 'none' : undefined }}>
        <ListPage
          icon={KeyIcon}
          title={tr('lic.title', 'Licencias y números de serie')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('lic.new', 'Nueva licencia')}
          createTitle={tr('lic.new_hint', 'Número de serie o clave de un producto de software')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('lic.search', 'Buscar por producto, proveedor, contrato, equipo…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="licencias"
          exportSheetName="Licencias"

          columns={columns}
          columnStorageKey="cmdb_cols_licenses"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(l) => l.id}
          rowId={(l) => `lic-row-${l.id}`}
          rowClassName={(l) => (l.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(l) => `${tr('action.view', 'Ver')} ${l.product}`}
          groupBy={groupBy}

          kpis={kpis}

          headerActions={copied && (
            <span className="list-badge list-badge--on" title={tr('lic.copied', 'Copiado al portapapeles')}>
              ✓ {tr('lic.copied_hint', 'Clave copiada')}
            </span>
          )}

          views={LIC_VIEWS}
          listKey="licenses"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando licencias…')}
          error={error}

          empty={{
            icon: '🗝️',
            title: licenses.length === 0
              ? tr('lic.empty', 'No hay licencias registradas')
              : tr('msg.no_results', 'Ninguna licencia coincide con los filtros'),
            subtitle: licenses.length === 0
              ? tr('lic.empty_hint', 'Apunta aquí los números de serie del software (SQL Server, Windows Server, backup…).')
              : undefined,
            action: licenses.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('lic.new', 'Nueva licencia')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (l) => {
              const ciPath = l.assigned_ci_id ? docElementPath(l.assigned_ci_type, l.assigned_ci_id) : null;
              return (
                <>
                  {/* Ver el equipo donde está instalada */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!ciPath}
                    onClick={() => ciPath && navigate(ciPath)}
                    title={l.assigned_ci_name
                      ? `${tr('lic.view_ci', 'Ver el equipo donde está instalada')}: ${l.assigned_ci_name}`
                      : tr('lic.installed_no', 'Sin equipo asignado')}
                  >
                    <ServerStackIcon />
                  </button>
                  {/* Copiar la clave al portapapeles */}
                  <button
                    type="button"
                    className="list-action-btn"
                    disabled={!l.license_key}
                    onClick={() => copyKey(l)}
                    title={tr('lic.copy_key', 'Copiar la clave al portapapeles')}
                  >
                    <ClipboardDocumentIcon />
                  </button>
                </>
              );
            },
            onCopy: openCopy,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('lic.delete_title', 'Eliminar licencia'),
            deleteMessage: (l) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{l.product}</strong>?{' '}
                {tr('lic.delete_used', 'Se perderá también el número de serie guardado.')}
              </>
            ),
          }}
        />
      </div>
    </>
  );
}
